<template>
  <div class="video-lib">
    <header class="toolbar">
      <h2>{{ $t('video.title') }}</h2>

      <p class="sub" v-if="!q">
        {{ $t('video.subtitle') }}
      </p>
      <p class="sub" v-else>
        {{ $t('video.searchResult', { count: matchedVideos.length }) }}
      </p>

      <div class="search">
        <input
          v-model.trim="q"
          type="search"
          :placeholder="$t('video.placeholder')"
          @keydown.enter.prevent="runSearch()"
        />
        <button @click="runSearch()" :disabled="searching">
          {{ $t('video.search') }}
        </button>
        <button v-if="q" class="ghost" @click="clearSearch">
          {{ $t('video.clear') }}
        </button>
      </div>
    </header>

    <section class="list">
      <div v-if="loading" class="hint">載入中…</div>

      <!-- 搜尋模式 -->
      <template v-else-if="q">
        <div v-if="matchedVideos.length === 0" class="hint">沒有符合的影片</div>

        <!-- 影片列表：16:9、點進去直接播放 -->
        <div v-else class="grid">
          <article
            v-for="v in matchedVideos"
            :key="v.id"
            class="vcard"
            @click="gotoPlay(v.id)"
          >
            <img :src="v.thumb_url || placeholder16x9" alt="" class="thumb16x9" loading="lazy" />
            <div class="meta">
              <div class="vtitle">{{ v.title }}</div>
              <div v-if="v.topic_name" class="vsub">主題：{{ v.topic_name }}</div>
            </div>
          </article>
        </div>
      </template>

      <!-- 一般模式：主題列表（維持 1:1） -->
      <template v-else>
        <div v-if="topics.length === 0" class="hint">目前沒有主題</div>
        <div class="list-col">
          <article
            v-for="t in topics"
            :key="t.id"
            class="row-card"
            @click="gotoTopic(t.id)"
          >
            <img :src="t.cover_url || placeholderSquare" alt="" class="cover" loading="lazy" />
            <div class="meta">
              <div class="title">{{ t.name }}</div>
              <div class="count">共 {{ t.video_count }} 部影片</div>
            </div>
          </article>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '@/lib/supabase'
import i18n from '@/i18n'   // ✅ 直接引入全域 i18n 實例

// 取出目前語言（兼容字串與 ref 兩種狀況）
const currentLocale = computed(() => {
  const l = i18n.global.locale
  return typeof l === 'string' ? l : l.value || 'zh-TW'
})
const topicNameMap = ref(new Map())

const router = useRouter()
const topics = ref([])
const loading = ref(false)
const q = ref('')
const searching = ref(false)

// ✅ 搜尋時用影片結果（不再顯示主題）
const matchedVideos = ref([])

const placeholderSquare = 'https://placehold.co/400x400?text=TSK+Topic'     // 主題 1:1
const placeholder16x9  = 'https://placehold.co/480x270?text=Video'          // 影片 16:9

// 🔁 REPLACE: loadTopics
async function loadTopics() {
  loading.value = true

  // 1) 先抓主題本體（不綁 i18n 條件，避免被過濾成 0 筆）
  const { data: rawTopics, error: e1 } = await supabase
    .from('video_topics')
    .select('id, sort, created_at, cover_url, videos:videos(count)')
    .order('sort', { ascending: true })
    .order('created_at', { ascending: false })

  if (e1) {
    console.error('[loadTopics] topics error:', e1)
    topics.value = []
    topicNameMap.value = new Map()
    loading.value = false
    return
  }

  const ids = (rawTopics || []).map(t => t.id)
  let lang = currentLocale.value

  // 2) 針對這些 topic_id 抓 i18n（當前語言 + zh-TW 做 fallback）
  const { data: i18nRows, error: e2 } = await supabase
    .from('video_topic_i18n')
    .select('topic_id, locale, name, cover_url')
    .in('topic_id', ids)
    .in('locale', [lang, 'zh-TW'])

  if (e2) {
    console.error('[loadTopics] i18n error:', e2)
  }

  // 3) 合併（先塞 zh-TW，再用當前語言覆蓋）
  const nameMap = new Map()
  const coverMap = new Map()

  ;(i18nRows || [])
    .filter(r => r.locale === 'zh-TW')
    .forEach(r => {
      nameMap.set(r.topic_id, r.name || '')
      coverMap.set(r.topic_id, r.cover_url || '')
    })

  ;(i18nRows || [])
    .filter(r => r.locale === lang)
    .forEach(r => {
      if (r.name) nameMap.set(r.topic_id, r.name)
      if (r.cover_url) coverMap.set(r.topic_id, r.cover_url)
    })

  // 4) 建立畫面資料
  topics.value = (rawTopics || []).map(t => ({
    id: t.id,
    name: nameMap.get(t.id) || '(未翻譯)',
    cover_url: coverMap.get(t.id) || t.cover_url || '',
    sort: t.sort,
    video_count: Array.isArray(t.videos) ? (t.videos[0]?.count ?? 0) : 0,
    created_at: t.created_at,
  }))

  // 讓搜尋結果也能用這份名稱快取
  topicNameMap.value = nameMap

  loading.value = false
}

function gotoTopic(id) {
  router.push({ name: 'TopicVideos', params: { topicId: id } })
}

function gotoPlay(id) {
  router.push({ name: 'VideoPlay', params: { id } })
}

/* ---- 即時搜尋：debounce 300ms，至少 2 字 ---- */
let debounceTimer = null
watch(q, (val) => {
  clearTimeout(debounceTimer)
  const kw = (val || '').trim()

  if (!kw) { matchedVideos.value = []; return }
  if (kw.length < 2) return

  debounceTimer = setTimeout(() => { runSearch(kw) }, 300)
})

onUnmounted(() => { clearTimeout(debounceTimer) })

// 🔁 REPLACE: runSearch
async function runSearch(passedKw) {
  const kw = String(passedKw || q.value || '').trim()
  if (!kw) { matchedVideos.value = []; return }

  searching.value = true
  try {
    const L = currentLocale.value
    const pattern = `%${kw}%`

    // 先查目前語系：只搜 text_keywords
    let { data, error } = await supabase
      .from('video_i18n')
      .select(`
        video_id,
        title,
        thumb_url,
        locale,
        videos!inner(topic_id, created_at, sort)
      `)
      .eq('locale', L)
      .ilike('text_keywords', pattern)                 // << 這裡
      .order('sort', { ascending: true,  foreignTable: 'videos' })
      .order('created_at', { ascending: false, foreignTable: 'videos' })
      .limit(200)

    if (error) { console.error('[search video_i18n]', error); data = [] }

    // 若沒結果 → 補 zh-TW（同樣搜 text_keywords）
    if ((!data || data.length === 0) && L !== 'zh-TW') {
      const fb = await supabase
        .from('video_i18n')
        .select(`
          video_id,
          title,
          thumb_url,
          locale,
          videos!inner(topic_id, created_at, sort)
        `)
        .eq('locale', 'zh-TW')
        .ilike('text_keywords', pattern)              // << 這裡
        .order('sort', { ascending: true,  foreignTable: 'videos' })
        .order('created_at', { ascending: false, foreignTable: 'videos' })
        .limit(200)
      if (!fb.error) data = fb.data || []
    }

    matchedVideos.value = (data || []).map(v => ({
      id: v.video_id,
      title: v.title,
      thumb_url: v.thumb_url || '',
      topic_id: v.videos?.topic_id || null,
      topic_name: v.videos?.topic_id ? (topicNameMap.value.get(v.videos.topic_id) || '') : '',
      created_at: v.videos?.created_at
    }))
  } finally {
    searching.value = false
  }
}

function clearSearch() {
  q.value = ''
  matchedVideos.value = []
  clearTimeout(debounceTimer)
}

onMounted(loadTopics)

watch(currentLocale, async (newLocale, oldLocale) => {
  if (newLocale !== oldLocale) {
    console.log(`[i18n] locale changed: ${oldLocale} → ${newLocale}`)
    await loadTopics()
  }
})

</script>

<style scoped>
.video-lib { display:flex; flex-direction:column; gap:16px; }
.toolbar { background:#fff; border:1px solid #eee; border-radius:14px; padding:12px; }
.sub { color:#666; margin-top:4px; }

.search {
  margin-top:10px;
  display:grid; grid-template-columns:1fr auto auto; gap:8px;
}
.search input { height:38px; border:1px solid #e5e7eb; border-radius:10px; padding:0 12px; }
.search button { height:38px; border-radius:10px; border:1px solid #e5e7eb; background:#111; color:#fff; padding:0 14px; cursor:pointer; }
.search .ghost { background:#fff; color:#333; }

.list { background:#fff; border:1px solid #eee; border-radius:14px; padding:14px; min-height:360px; }
.hint { color:#666; padding:20px; }

/* 一般模式：主題 1:1 卡 */
.list-col { display:flex; flex-direction:column; gap:12px; }
.row-card {
  display:flex; align-items:center;
  background:#fafafa; border:1px solid #eee; border-radius:12px;
  cursor:pointer; overflow:hidden;
  transition: transform .12s ease, box-shadow .12s ease;
}
.row-card:hover { transform:translateY(-1px); box-shadow:0 10px 24px rgba(0,0,0,.06); }
.cover {
  width: 200px;
  aspect-ratio: 1 / 1;      /* 主題維持 1:1 */
  object-fit: cover;
  flex-shrink: 0;
  background: #ddd;
}
.meta { padding:10px 14px; display:flex; flex-direction:column; align-items: flex-start; }
.title { font-weight:600; margin-bottom:6px; }
.count { color:#666; font-size:14px; text-align:left; align-self:flex-start; }

/* 搜尋模式：影片 16:9 卡 */
.grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; }
@media (max-width:1200px){ .grid{ grid-template-columns: repeat(2, minmax(0,1fr)); } }
.vcard { background:#fafafa; border:1px solid #eee; border-radius:12px; overflow:hidden; cursor:pointer; transition: transform .12s ease, box-shadow .12s ease; }
.vcard:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.06); }
.thumb16x9 { width:100%; aspect-ratio:16/9; object-fit:cover; display:block; background:#ddd; }
.vtitle { font-weight:600; margin:8px 12px 4px; }
.vsub { color:#666; font-size:13px; margin:0 12px 10px; }
</style>
